/**
 * Patient linkage for tasks — the badge half.
 *
 * A task is linked to a patient in TWO places that must agree (see design doc):
 *   1. a badge <span data-teamio-patientid="…" class="badge mr-xs badge-secondary badge-pill mt-1">
 *      inside task.content — TodoItem.vue / TodoDetails.vue re-parse it to draw the chip;
 *   2. task.taggedPatients: number[] — what tasks/getTasksTaggedWithPatient queries with
 *      array-contains (type-strict: 8814 !== "8814").
 *
 * KEEP THIS FILE IMPORT-FREE: cypress/node-tests/task-patients.test.js executes it directly.
 */

export function getTaggedPatientIDs(contentHTML) {
  if (!contentHTML || typeof contentHTML !== 'string') return [];
  const regex = /data-teamio-patientid="([0-9]+)/g;
  const ids = [];
  let m;
  while ((m = regex.exec(contentHTML)) !== null) ids.push(parseInt(m[1], 10));
  return ids;
}

export function hasPatientTag(contentHTML, patNum) {
  const n = Number(patNum);
  if (!Number.isFinite(n)) return false;
  return getTaggedPatientIDs(contentHTML).includes(n);
}

function escapeHtml(value) {
  return String(value == null ? '' : value)
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}

/** "LName, FName" — the same label the roster's Patient column / # picker shows. */
export function patientDisplayName({ LName, FName } = {}) {
  const l = (LName || '').trim();
  const f = (FName || '').trim();
  if (l && f) return `${l}, ${f}`;
  return l || f;
}

/**
 * The exact structure the <at> picker embeds (ToDo.vue:127-133 rendered), so the
 * list/detail parsers and the profile link behave identically for seeded badges.
 */
export function buildPatientBadgeHTML({ siteID, PatNum, LName, FName }) {
  const id = String(PatNum == null ? '' : PatNum).trim();
  // Validate rather than escape: task.content is rendered with v-html (TaskSearch.vue),
  // and the read-back regex only recognises digits anyway — so a PatNum that is not pure
  // digits can only be an attribute-injection attempt. Refuse it.
  if (!/^[0-9]+$/.test(id)) throw new Error('buildPatientBadgeHTML: PatNum must be numeric');
  const label = escapeHtml(patientDisplayName({ LName, FName }) || `Patient ${id}`);
  const href = `/app/patientprofile/${encodeURIComponent(String(siteID || ''))}-${id}`;
  return `<span data-at-embedded="" contenteditable="false"> <span data-teamio-patientid="${id}" class="badge mr-xs badge-secondary badge-pill mt-1"><a href="${href}" class="">${label}</a></span> </span>`;
}

/** Idempotent: returns the input unchanged when the patient is already tagged. */
export function ensurePatientTagged(contentHTML, patient) {
  const html = contentHTML || '';
  if (!patient) return html;
  const id = String(patient.PatNum == null ? '' : patient.PatNum).trim();
  // A missing / non-numeric PatNum is treated like "no patient" — never throw here.
  if (!/^[0-9]+$/.test(id)) return html;
  if (hasPatientTag(html, id)) return html;
  return `${html}&nbsp;${buildPatientBadgeHTML(patient)}`;
}

/** Human text only: badges removed, tags stripped, &nbsp; decoded, whitespace collapsed. */
export function stripMentions(contentHTML) {
  if (!contentHTML || typeof contentHTML !== 'string') return '';
  return contentHTML
    .replace(/<span[^>]*data-at-embedded=""[^>]*>[\s\S]*?<\/span>\s*<\/span>/g, ' ')
    .replace(/<[^>]+>/g, ' ')
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&').replace(/&lt;/g, '<').replace(/&gt;/g, '>').replace(/&quot;/g, '"')
    .replace(/\s+/g, ' ')
    .trim();
}

export function hasTaskText(contentHTML) {
  return stripMentions(contentHTML).length > 0;
}
